<template>
  <div class="tc" :class="{ 'tc--dark': $vuetify.theme.dark }">
    <!-- NAV BAR -->
    <header class="tc-nav">
      <h1 class="tc-nav__title">Tutorlar</h1>

      <div class="tc-nav__right">
        <v-text-field
          :placeholder="$t('Izlash...')"
          append-icon="mdi-magnify"
          v-model="search"
          dense
          hide-details
          outlined
          clearable
          class="search-input"
          style="max-width: 320px;"
          @click:append="getItems"
          @keyup.enter="getItems"
          @click:clear="search = ''; getItems()"
        />

        <button v-if="canCreate" class="tc-btn tc-btn--primary font-made" @click="onAddModel">
          <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16"><path fill-rule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clip-rule="evenodd"/></svg>
          <span>{{ $t("Qo'shish") }}</span>
        </button>
      </div>

      <div v-if="isLoading" class="tc-nav__progress"></div>
    </header>

    <!-- TABLE CARD -->
    <div class="tc-card">
      <!-- Skeleton -->
      <template v-if="isLoading && items.length === 0">
        <div v-for="n in 6" :key="'sk'+n" class="tc-skel">
          <div class="tc-skel__bar" style="width:30px"></div>
          <div class="tc-skel__bar" style="width:220px"></div>
          <div class="tc-skel__bar" style="width:160px"></div>
          <div class="tc-skel__bar" style="width:60px"></div>
        </div>
      </template>

      <!-- Empty -->
      <div v-else-if="!isLoading && items.length === 0" class="tc-empty">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" width="48" height="48"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></svg>
        <p class="tc-empty__title">{{ $t("Ma'lumot topilmadi") }}</p>
        <p class="tc-empty__sub">{{ $t("Yangi o'qituvchi qo'shing") }}</p>
      </div>

      <!-- Data -->
      <table v-else class="tc-table">
        <thead>
          <tr>
            <th class="tc-th" style="width:50px">№</th>
            <th class="tc-th">{{ $t("F.I.O") }}</th>
            <th class="tc-th">{{ $t("Fan") }}</th>
            <th class="tc-th" style="width:80px"></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(item, index) in items" :key="item.id" class="tc-row">
            <td class="tc-td tc-td--num">{{ (options.page - 1) * itemsPerPage + index + 1 }}</td>
            <td class="tc-td">
              <div class="tc-teacher">
                <div class="tc-teacher__avatar">{{ initials(item.name) }}</div>
                <span class="tc-teacher__name">{{ item.name }}</span>
              </div>
            </td>
            <td class="tc-td tc-td--subject">{{ item.subject || '—' }}</td>
            <td class="tc-td">
              <div class="tc-actions">
                <button v-if="canUpdate" class="tc-act" :title="$t('Tahrirlash')" @click="onEdit({ item })">
                  <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16"><path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z"/></svg>
                </button>
                <button v-if="canDelete" class="tc-act tc-act--delete" :title="$t(`O'chirish`)" @click="confirmDelete(item)">
                  <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16"><path fill-rule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clip-rule="evenodd"/></svg>
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Pagination -->
      <div v-if="totalItemsCount > itemsPerPage" class="tc-pagination">
        <span class="tc-pagination__info">{{ totalItemsCount }} {{ $t("ta") }}</span>
        <v-pagination
          v-model="options.page"
          :length="Math.ceil(totalItemsCount / itemsPerPage)"
          :total-visible="5"
          circle
          class="tc-pagination__nav"
        />
      </div>
    </div>

    <!-- MODAL -->
    <teacher-add
      v-if="isAddModal"
      @close="isAddModal = false"
      @refresh="getItems"
      :data="selected"
    />

    <!-- DELETE CONFIRM -->
    <transition name="tc-modal-fade">
      <div v-if="deleteTarget" class="tc-modal" role="dialog" aria-modal="true" @click.self="deleteTarget = null">
        <div class="tc-modal-card tc-modal-card--small">
          <div class="tc-modal-header">
            <h3>{{ $t("O'qituvchini o'chirish") }}</h3>
          </div>
          <div class="tc-modal-body">
            <p><b>{{ deleteTarget.name }}</b> {{ $t("o'chirib tashlaysizmi? O'chirilgandan keyin ortga qaytarib bo'lmaydi.") }}</p>
          </div>
          <div class="tc-modal-footer">
            <button class="tc-ghost" @click="deleteTarget = null">{{ $t("Bekor qilish") }}</button>
            <button class="tc-danger" @click="onDelete">{{ $t("O'chirib tashlash") }}</button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import axios from "axios";
import TeacherAdd from "../../components/modals/TeacherAdd.vue";

export default {
  components: { TeacherAdd },

  data: () => ({
    isLoading: false,
    options: { page: 1 },
    totalItemsCount: 0,
    itemsPerPage: 50,
    search: null,
    isAddModal: false,
    selected: {},
    deleteTarget: null,
    canCreate: false,
    canUpdate: false,
    canDelete: false,
  }),

  async mounted() {
    this.canCreate = await this.hasRole("TUTORLAR", "CREATE");
    this.canUpdate = await this.hasRole("TUTORLAR", "UPDATE");
    this.canDelete = await this.hasRole("TUTORLAR", "DELETE");
  },

  computed: {
    ...mapGetters("admin-teachers", ["GET_TEACHERS"]),
    items: (vm) => vm.GET_TEACHERS,
  },

  watch: {
    'options.page'() {
      this.getItems();
    },
  },

  created() {
    this.getItems();
  },

  methods: {
    ...mapActions("admin-teachers", ["INIT_TEACHERS", "DELETE_TEACHER"]),

    getItems() {
      if (this.isLoading) return;
      this.isLoading = true;

      this.INIT_TEACHERS({
        ...this.options,
        itemsPerPage: this.itemsPerPage,
        search: this.search,
      }).then((totalItemsCount) => {
        this.totalItemsCount = totalItemsCount;
        this.isLoading = false;
      });
    },

    onAddModel() {
      this.selected = {};
      this.$nextTick(() => { this.isAddModal = true; });
    },

    onEdit({ item = {} }) {
      this.selected = item;
      this.$nextTick(() => { this.isAddModal = true; });
    },

    confirmDelete(item) {
      this.deleteTarget = item;
    },

    onDelete() {
      if (!this.deleteTarget) return;
      this.DELETE_TEACHER(this.deleteTarget.id).then(() => {
        this.deleteTarget = null;
        this.getItems();
      });
    },

    initials(name) {
      if (!name) return '?';
      return name.split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase();
    },

    async hasRole(panel, op) {
      try {
        const res = await axios.get(`/admin/panel-permissions/check`, {
          params: { appPanel: panel, op }
        });
        return res.data === true;
      } catch {
        return false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
/* ══════ DESIGN TOKENS ══════ */
.tc {
  --bg: #ffffff;
  --card: #fff;
  --card-hover: #fafbfc;
  --border: rgba(0,0,0,.06);
  --text: #0f172a;
  --text2: #475569;
  --muted: #94a3b8;
  --accent: #ffd451;
  --accent-hover: #f5c542;
  --radius: 14px;
  --radius-sm: 10px;
  --shadow: 0 1px 3px rgba(0,0,0,.04), 0 4px 14px rgba(0,0,0,.03);

  min-height: 100vh;
  padding: 0 24px 40px;
  background: var(--bg);
  color: var(--text);
  font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Segoe UI', Roboto, sans-serif;
}

.tc--dark {
  --bg: #111318;
  --card: #1a1b23;
  --card-hover: #22232e;
  --border: rgba(255,255,255,.06);
  --text: #e4e5eb;
  --text2: #9ca3af;
  --muted: #606275;
  --shadow: 0 2px 8px rgba(0,0,0,.3);
}

/* ══════ NAV ══════ */
.tc-nav {
  position: relative;
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 0;
  margin-bottom: 16px;
}

.tc-nav__title {
  font-size: 20px;
  font-weight: 700;
  letter-spacing: -0.01em;
  margin: 0;
  white-space: nowrap;
}

.tc-nav__right {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-left: auto;
}

.tc-nav__progress {
  position: absolute; bottom: 0; left: 0; right: 0; height: 2px;
  background: linear-gradient(90deg, var(--accent) 30%, transparent 30%);
  animation: tc-progress 1s ease infinite;
}
@keyframes tc-progress {
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
}

/* ══════ SEARCH ══════ */
.search-input {
  max-width: none;
}

::v-deep .search-input {
  .v-input__slot {
    background: #f1f5f9 !important;
    border-radius: 10px !important;
    min-height: 38px !important;
  }
  fieldset {
    border-color: transparent !important;
  }
  &:hover fieldset {
    border-color: #d0d5dd !important;
  }
  &.v-input--is-focused fieldset {
    border-color: #00274c !important;
    border-width: 1.5px !important;
  }
  .v-icon {
    color: #94a3b8 !important;
    font-size: 20px !important;
  }
  input {
    font-size: 14px;
  }
  input::placeholder {
    color: #94a3b8 !important;
  }
}

::v-deep .search-input.theme--dark {
  .v-input__slot {
    background: #22232e !important;
  }
  fieldset {
    border-color: transparent !important;
  }
  &:hover fieldset {
    border-color: #33354a !important;
  }
  &.v-input--is-focused fieldset {
    border-color: #ffd451 !important;
  }
  .v-icon {
    color: #606275 !important;
  }
  input {
    color: #d5d6de !important;
  }
  input::placeholder {
    color: #606275 !important;
  }
}

/* ══════ BUTTONS ══════ */
.tc-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  background: var(--card);
  color: var(--text);
  padding: 8px 14px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all .15s;
  white-space: nowrap;
}
.tc-btn:hover { border-color: var(--accent); }

.tc-btn--icon {
  padding: 8px 10px;
}
.tc-btn--icon.is-spin svg {
  animation: tc-spin .8s linear infinite;
}
@keyframes tc-spin {
  to { transform: rotate(360deg); }
}

.tc-btn--primary {
  background: var(--accent);
  border-color: var(--accent);
  color: #0f172a;
  font-weight: 600;
}
.tc-btn--primary:hover {
  background: var(--accent-hover);
  border-color: var(--accent-hover);
}

/* ══════ CARD ══════ */
.tc-card {
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  box-shadow: var(--shadow);
  overflow: hidden;
}

/* ══════ TABLE ══════ */
.tc-table {
  width: 100%;
  border-collapse: collapse;
}

.tc-th {
  text-align: left;
  padding: 12px 16px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--muted);
  border-bottom: 1px solid var(--border);
  white-space: nowrap;
}

.tc-row {
  transition: background .1s;
  &:hover { background: var(--card-hover); }
  &:not(:last-child) .tc-td { border-bottom: 1px solid var(--border); }
}

.tc-td {
  padding: 14px 16px;
  font-size: 14px;
  color: var(--text);
  vertical-align: middle;
}

.tc-td--num {
  color: var(--muted);
  font-size: 13px;
  font-variant-numeric: tabular-nums;
}

.tc-td--subject {
  color: var(--text2);
}

/* ══════ TEACHER CELL ══════ */
.tc-teacher {
  display: flex;
  align-items: center;
  gap: 12px;
}

.tc-teacher__avatar {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: linear-gradient(135deg, var(--accent), #f5c542);
  color: #0f172a;
  font-size: 13px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.tc-teacher__name {
  font-weight: 600;
  font-size: 14px;
}

/* ══════ ACTIONS ══════ */
.tc-act {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 8px;
  border: 1px solid var(--border);
  background: none;
  color: var(--text2);
  cursor: pointer;
  transition: all .15s;
}
.tc-act:hover {
  border-color: var(--accent);
  color: var(--accent);
  background: rgba(255,212,81,.06);
}

.tc-act--delete:hover {
  border-color: #ef4444;
  color: #ef4444;
  background: rgba(239,68,68,.06);
}

.tc-actions {
  display: flex;
  gap: 6px;
}

/* ══════ DELETE MODAL (matches admin-messages) ══════ */
.tc-modal { position: fixed; inset: 0; background: rgba(0,0,0,.35); backdrop-filter: blur(16px); -webkit-backdrop-filter: blur(16px); display: grid; place-items: center; padding: 20px; z-index: 200; }
.tc-modal-card { width: 100%; max-width: 820px; max-height: 85vh; background: #fff; border-radius: 16px; border: 1px solid #e5e7eb; box-shadow: 0 10px 30px rgba(0,0,0,.12); display: flex; flex-direction: column; overflow: hidden; }
.tc-modal-card--small { max-width: 480px; }
.tc-modal-header { display: flex; align-items: center; justify-content: space-between; padding: 14px 20px; border-bottom: 1px solid #e5e7eb; flex-shrink: 0; }
.tc-modal-header h3 { margin: 0; font-size: 1.1rem; font-weight: 700; color: #111827; }
.tc-modal-body { padding: 20px; display: grid; gap: 16px; overflow-y: auto; min-height: 0; }
.tc-modal-body p { margin: 0; font-size: .95rem; color: #4b5563; line-height: 1.5; }
.tc-modal-footer { display: flex; justify-content: flex-end; gap: 10px; padding: 14px 20px; border-top: 1px solid #e5e7eb; flex-shrink: 0; }

.tc-ghost, .tc-danger { border: none; border-radius: 12px; padding: 10px 14px; font-weight: 600; cursor: pointer; transition: background .15s ease, border-color .15s ease, color .15s ease; display: inline-flex; align-items: center; gap: 8px; font-size: .9rem; }
.tc-danger { background: #dc2626; color: #fff; }
.tc-danger:hover { opacity: .9; }
.tc-ghost { background: #fff; color: #111827; border: 1.5px dashed #d1d5db; }
.tc-ghost:hover { border-color: #00274c; color: #00274c; }

/* Dark mode */
.tc--dark .tc-modal { background: rgba(0,0,0,.40); }
.tc--dark .tc-modal-card { background: #1a1b23; border-color: #2a2c3a; box-shadow: 0 10px 30px rgba(0,0,0,.4); }
.tc--dark .tc-modal-header { border-bottom-color: #2a2c3a; }
.tc--dark .tc-modal-header h3 { color: #e4e5eb; }
.tc--dark .tc-modal-footer { border-top-color: #2a2c3a; }
.tc--dark .tc-modal-body p { color: #d5d6de; }
.tc--dark .tc-ghost { background: #1a1b23; color: #d5d6de; border-color: #2a2c3a; }
.tc--dark .tc-ghost:hover { border-color: #ffd451; color: #ffd451; }
.tc--dark .tc-danger { background: #dc2626; color: #fff; }
.tc--dark .tc-danger:hover { opacity: .9; }

.tc-modal-fade-enter-active, .tc-modal-fade-leave-active {
  transition: opacity .2s ease;
}
.tc-modal-fade-enter, .tc-modal-fade-leave-to {
  opacity: 0;
}

/* ══════ SKELETON ══════ */
.tc-skel {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  &:not(:last-child) { border-bottom: 1px solid var(--border); }
}

.tc-skel__bar {
  height: 14px;
  border-radius: 6px;
  background: var(--border);
  animation: tc-shimmer 1.4s ease infinite;
}

@keyframes tc-shimmer {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
}

/* ══════ EMPTY ══════ */
.tc-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 24px;
  color: var(--muted);
}

.tc-empty__title {
  font-size: 16px;
  font-weight: 600;
  color: var(--text2);
  margin: 16px 0 4px;
}

.tc-empty__sub {
  font-size: 13px;
  color: var(--muted);
  margin: 0;
}

/* ══════ PAGINATION ══════ */
.tc-pagination {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-top: 1px solid var(--border);
}

.tc-pagination__info {
  font-size: 13px;
  color: var(--muted);
  font-variant-numeric: tabular-nums;
}

.tc-pagination__nav ::v-deep .v-pagination__item,
.tc-pagination__nav ::v-deep .v-pagination__navigation {
  box-shadow: none !important;
  border: 1px solid var(--border);
  background: var(--card) !important;
  color: var(--text) !important;
  font-size: 13px;
}

.tc-pagination__nav ::v-deep .v-pagination__item--active {
  background: var(--accent) !important;
  border-color: var(--accent) !important;
  color: #0f172a !important;
}

@media (max-width: 600px) {
  .tc-nav { flex-wrap: wrap; gap: 10px; }
  .tc-nav__right { width: 100%; margin-left: 0; }
  .tc-nav__right .search-input { max-width: none !important; flex: 1; }
  .tc { padding-left: 12px; padding-right: 12px; }
}
</style>
